// @ts-nocheck
/* eslint-disable camelcase */
import { mapObj } from '../../utils/obj'
import { pathname, location, brand } from '../app-selectors'
import {
  findCalculatedValuesInSchema,
  generateValueCalculated,
} from '../../server/helpers/elasticsearch/calculator-functions'
import { FsCore } from '../../types/fs-core'
import { useSelector } from 'react-redux'

export const prepareECLinkParams = (eligibility: any) => {
  return eligibility
    ? [
        {
          parameterKey: 'secure_product_token',
          // secure_product_token is a special case, it goes inside the 'r' redirect parameter,
          // so it has to first form-encode and then url encode
          value: encodeURIComponent(eligibility.secureProductToken),
          condition: (link, value) =>
            link.destination_url && link.destination_url.includes('secure_product_token') && value,
        },
        {
          parameterKey: 'external_check_id',
          value: eligibility.flowId,
          condition: (link, value) => !!value,
        },
        {
          parameterKey: 'check_provider',
          value: eligibility.provider,
          condition: (link, value) => !!value,
        },
      ]
    : []
}

const generalLinkParams = (_id, siteBrand) => [
  {
    parameterKey: 'product_id',
    value: _id,
    condition: (link, value) => !!value,
  },
  {
    parameterKey: 'platform',
    value: `fs-core-${siteBrand}`,
    condition: (link, value) => !!value,
  },
]

export const hydrateProductLink = (link, linkParameters) => {
  if (link.destination_url) {
    const hydratedUrl = linkParameters.reduce((url, param) => {
      if (param.condition(link, param.value) && link.destination_url) {
        url.searchParams.append(param.parameterKey, param.value)
      }
      return url
    }, new URL(link.destination_url))

    return hydratedUrl.toString()
  }
}

const addPageQueryParam = (link: string, s: FsCore.State) => {
  const page = pathname(s)

  if (page === '') {
    // eslint-disable-next-line no-console
    console.log(`No page found for ${link}, location: ${JSON.stringify(location(s))}`)
  }

  return `${link}${link.includes('?') ? '&' : '?'}page=${page}`
}

const appendAdditionalDataToLink =
  (state: FsCore.State, eligibility: any, _id: any) => (link: FsCore.ESLink) => {
    if (typeof link !== 'object' || link === null) return

    const rawLink = link.destination_url
    if (!rawLink) return link

    const url = addPageQueryParam(rawLink, state)

    const hydratedUrl = hydrateProductLink(
      {
        ...link,
        // eslint-disable-next-line
        destination_url: url,
      },
      [...prepareECLinkParams(eligibility), ...generalLinkParams(_id, brand(state))],
    )

    return {
      ...link,
      // eslint-disable-next-line
      destination_url: hydratedUrl,
    }
  }

const appendAdditionalDataToLinks =
  (state: FsCore.State, eligibility: any, _id: any) =>
  (brandLinks: FsCore.ESProviderLinks = {}) =>
    mapObj(appendAdditionalDataToLink(state, eligibility, _id), brandLinks)

const useESQuery = (queryId: string): FsCore.ESQueryState | undefined =>
  useSelector((state) => state.elasticsearch).queries[queryId]

export const useESData = (queryId: string): FsCore.ESQueryData | undefined =>
  useESQuery(queryId)?.data

const getESState = (s: FsCore.State) => s.elasticsearch

const getESProductDataSet = (s: FsCore.State, queryId: string): FsCore.ESQueryState | undefined =>
  getESState(s).productDataSet[queryId]
export const getESQuery = (s: FsCore.State, queryId: string): FsCore.ESQueryState | undefined =>
  getESState(s).queries[queryId]

export const getESData = (s: FsCore.State, queryId: string): FsCore.ESQueryData | undefined =>
  getESQuery(s, queryId)?.data

export const getESProductDataSetData = (
  s: FsCore.State,
  queryId: string,
): FsCore.ESQueryData | undefined => getESProductDataSet(s, queryId)?.data

type ProductDataSetID = string

const hits = (s: FsCore.State, key: ProductDataSetID) =>
  (getESData(s, key) && getESData(s, key)?.hits?.hits) || []

export const sources = (s: FsCore.State, key: string) => {
  const calculatorId = getESQuery(s, key)?.calculatorId
  if (!calculatorId) {
    // eslint-disable-next-line no-console
    console.error(`No ES query state or calculator id for ${key}`)
  }

  return hits(s, key).map(({ _source = {}, eligibility, _id }) => ({
    ..._source,
    links: mapObj(appendAdditionalDataToLinks(s, eligibility, _id), _source.links || {}),
  }))
}

const parsedHits = (s: FsCore.State, key: string) => {
  const calculatorId = getESQuery(s, key)?.calculatorId
  if (!calculatorId) {
    // eslint-disable-next-line no-console
    console.error(`No ES query state or calculator id for ${key}`)
  }

  return hits(s, key).map((hit = {} as FsCore.ESHit) => ({
    ...hit,
    _source: {
      ...(hit._source || {}),
      links: mapObj(
        appendAdditionalDataToLinks(s, hit.eligibility, hit._id),
        hit._source?.links || {},
      ),
    },
  }))
}

/** Generates the list of parsed ES hits, with new added fields
 * coming from our own code (currently only calculated values).
 * This allows product tables to display programmatically generated fields.
 */
export const parsedHitsWithExtraData = (
  state: FsCore.State,
  calculatorInputs: FsCore.CalculatorInputs | undefined,
  schema: any,
  key: string,
) => {
  const hits = parsedHits(state, key)
  if (!calculatorInputs) {
    return hits
  }
  const calculatedValues = findCalculatedValuesInSchema(schema)
  for (const hit of hits) {
    if (calculatedValues.length) {
      hit.calculatedValue = {}
      for (const functionName of calculatedValues) {
        hit.calculatedValue[functionName] = generateValueCalculated(
          functionName,
          calculatorInputs,
          hit,
          hit.calculatedValue,
        )
      }
    }
  }
  return hits
}

export const product = (s: FsCore.State) => getESState(s).product
